import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { TextField, SortMenu, Select, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer,
} from "./Filters.styled";

const Filters = ({
  onSortChange,
  onOrderChange,
  selectedSortOption = "timestamp",
  orderDirection = "desc",
  onSearchChange,
  onActionChange,
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedAction, setSelectedAction] = useState(null);
  const searchTimeoutRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 500);
  }, [onSearchChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleActionChange = useCallback((value, option) => {
    setSelectedAction(option);
    onActionChange?.(value);
  }, [onActionChange]);

  const handleClearFilters = useCallback(() => {
    setSelectedAction(null);
    setSearchValue("");
    setFiltersKey((prev) => prev + 1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    onActionChange?.(null);
    onSearchChange?.("");
  }, [onActionChange, onSearchChange]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!selectedAction;
  }, [selectedAction]);

  // Action options based on knowledge activity log
  const actionOptions = [
    { label: "Created", value: "Created" },
    { label: "Updated", value: "Updated" },
    { label: "Modified", value: "Modified" },
    { label: "Deleted", value: "Deleted" },
  ];

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchValue}
            disabled={loading}
          />
        </SearchContainer>

        <Select 
          key={`action-filter-${filtersKey}`}
          label="Action" 
          placeholder={selectedAction?.label || "Action"} 
          onChange={handleActionChange}
          size="small"
          width="auto"
          minWidth="100px"
          disabled={loading}
        >
          <Select.Menu>
            {actionOptions.map((action) => (
              <Select.Item 
                key={action.value} 
                value={action.value} 
                label={action.label}
              >
                {action.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>

        {hasActiveFilters && (
          <Button
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearFilters}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>

      <FiltersRightContent>
        <SortMenu
          minWidth="200px"
          onChange={(id) => {
            if (onSortChange) {
              onSortChange(id);
            }
          }}
          order={orderDirection ? orderDirection.toLowerCase() : "desc"}
          onOrderChange={(newOrder) => {
            if (onOrderChange) {
              onOrderChange(newOrder);
            }
          }}
          options={[
            {
              icon: 'clock',
              id: 'timestamp',
              label: 'Most Recent'
            },
            {
              icon: 'category',
              id: 'fact',
              label: 'Fact'
            },
            {
              icon: 'sortByAlpha',
              id: 'user',
              label: 'User'
            },
            {
              icon: 'label',
              id: 'action',
              label: 'Action'
            }
          ]}
          selected={selectedSortOption || "timestamp"}
          size="medium"
          title="SORT BY"
          width="240px"
          disabled={loading}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;
